import { ExpandMore } from "@mui/icons-material";
import {
  Box,
  styled,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { sortBy, uniq } from "lodash";
import { ReactElement, useEffect, useMemo, useState } from "react";

import {
  CalculateCampaignOverlapsMutation,
  CalculateCampaignOverlapsMutationVariables,
  CampaignSortBy,
  CampaignStatus,
  CreateSuppressionsFromCampaignOverlapsMutation,
  CreateSuppressionsFromCampaignOverlapsMutationVariables,
  OverlapSuppressionFilterType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  CALCULATE_CAMPAIGN_OVERLAPS,
  CREATE_SUPPRESSIONS_FROM_CAMPAIGN_OVERLAPS,
} from "~frontend/graphql/campaigns";
import { CAMPAIGN_SORTS } from "~frontend/routes/subaccounts/campaignsList/constants";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import { palette } from "~frontend/theme";
import { jobFailed } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";
import { isSuppressionJobRunning } from "../suppressionJobs";
import InlineTextField from "./InlineTextField";
import OverlapFilterDialog from "./OverlapFilterDialog";
import OverlapListControls from "./OverlapListControls";
import OverlapTable from "./OverlapTable";

const StyledAccordion = styled(Accordion)({
  backgroundColor: palette.lightestGray,
  paddingBottom: 0,
});

export default function OverlapList({
  campaign,
  expanded,
  onToggleExpanded,
  onApplySuppressions,
}: {
  campaign: CampaignWithSuppressionData;
  expanded: boolean;
  onToggleExpanded: () => void;
  onApplySuppressions: () => void;
}): ReactElement {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState(
    Object.values(CampaignStatus)
  );
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<CampaignSortBy>(CAMPAIGN_SORTS[0].value);

  const [calculateOverlaps, { loading: calculateOverlapsLoading }] =
    useMutation<
      CalculateCampaignOverlapsMutation,
      CalculateCampaignOverlapsMutationVariables
    >(CALCULATE_CAMPAIGN_OVERLAPS, {
      onErrorSnackbarMessage: "Failed to calculate campaign overlaps",
    });

  const anySuppressionJobRunning =
    calculateOverlapsLoading || isSuppressionJobRunning(campaign);

  const [createdAtWindowDays, setCreatedAtWindowDays] = useState(
    campaign.calculateCampaignOverlapsJob?.args.createdAtWindowDays ?? 7
  );
  const lastRecalculatedAt = campaign.calculateCampaignOverlapsJob?.finishedAt;

  const createdAtWindowDaysIsValid =
    !Number.isNaN(createdAtWindowDays) &&
    createdAtWindowDays > 0 &&
    createdAtWindowDays <= 365;

  const createdAtWindowChanged =
    !!campaign.calculateCampaignOverlapsJob &&
    createdAtWindowDaysIsValid &&
    createdAtWindowDays !==
      campaign.calculateCampaignOverlapsJob?.args.createdAtWindowDays;

  async function handleRecalculate() {
    if (!createdAtWindowDaysIsValid) {
      return;
    }

    await calculateOverlaps({
      variables: {
        input: {
          campaignId: campaign.id,
          createdAtWindowDays: createdAtWindowDays,
        },
      },
    });
  }

  const filteredOverlaps = useMemo(
    () =>
      applyFilters({
        overlaps: campaign.campaignOverlaps,
        statusFilter,
        search,
        sort,
      }),
    [campaign.campaignOverlaps, statusFilter, search, sort]
  );

  const [createSuppressionsFromOverlaps] = useMutation<
    CreateSuppressionsFromCampaignOverlapsMutation,
    CreateSuppressionsFromCampaignOverlapsMutationVariables
  >(CREATE_SUPPRESSIONS_FROM_CAMPAIGN_OVERLAPS, {
    onErrorSnackbarMessage: "Failed to suppress contacts",
  });

  const [suppressSubmitting, setSuppressSubmitting] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedOverlapIds, setSelectedOverlapIds] = useState<string[]>([]);

  useEffect(() => {
    // remove any selected rows that no longer exist
    const validOverlapIds = new Set(filteredOverlaps.map(o => o.id));
    const validSelectedIds = uniq(
      selectedOverlapIds.filter(overlapId => validOverlapIds.has(overlapId))
    );

    if (validSelectedIds.length !== selectedOverlapIds.length) {
      setSelectedOverlapIds(validSelectedIds);
    }
  }, [selectedOverlapIds, filteredOverlaps]);

  async function submitSuppressions(
    overlapIds: string[],
    filter: OverlapSuppressionFilterType | null,
    filterTagIds?: string[] | null
  ) {
    setSuppressSubmitting(true);
    setFilterDialogOpen(false);
    try {
      await createSuppressionsFromOverlaps({
        variables: {
          input: {
            campaignId: campaign.id,
            overlapIds,
            filter,
            filterTagIds,
          },
        },
      });
      setSelectedOverlapIds([]);
      onApplySuppressions();
    } finally {
      setSuppressSubmitting(false);
    }
  }

  function handleSuppressSelected() {
    return submitSuppressions(selectedOverlapIds, null);
  }

  async function handleSuppressFiltered({
    filter,
    filterTagIds,
  }: {
    filter: OverlapSuppressionFilterType | "";
    filterTagIds: string[] | null;
  }) {
    if (filter === "") {
      return;
    }

    return submitSuppressions(selectedOverlapIds, filter, filterTagIds);
  }

  function handleSuppressAll() {
    return submitSuppressions(
      filteredOverlaps.map(o => o.id),
      null
    );
  }

  if (
    !campaign.calculateCampaignOverlapsJob ||
    jobFailed(campaign.calculateCampaignOverlapsJob).failed
  ) {
    return (
      <FlexBox
        sx={{
          backgroundColor: palette.lightGray,
          padding: 3,
        }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          Find overlaps with campaigns created within the last{" "}
          <InlineTextField
            variant="standard"
            sx={{
              "& .MuiInputBase-root": { fontSize: "14px" },
              marginTop: "-3.5px",
            }}
            value={String(createdAtWindowDays)}
            onChange={e => setCreatedAtWindowDays(parseInt(e.target.value))}
            inputProps={{
              type: "number",
              min: 1,
              max: 365,
            }}
          />{" "}
          days
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRecalculate}
            disabled={calculateOverlapsLoading || !createdAtWindowDaysIsValid}
          >
            {calculateOverlapsLoading ? "Calculating..." : "Calculate Overlaps"}
          </Button>
        </Box>
      </FlexBox>
    );
  }

  return (
    <>
      <StyledAccordion
        disableGutters
        expanded={expanded}
        onChange={() => onToggleExpanded()}
        sx={{
          "& .MuiTableCell-footer .MuiTablePagination-spacer": {
            flex: "none",
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body1Bold">Previous Campaigns</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OverlapListControls
            campaign={campaign}
            onClickRecalculate={handleRecalculate}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            search={search}
            setSearch={setSearch}
            sort={sort}
            setSort={setSort}
            createdAtWindowDays={createdAtWindowDays}
            lastRecalculatedAt={
              lastRecalculatedAt ? new Date(lastRecalculatedAt) : null
            }
            setCreatedAtWindowDays={setCreatedAtWindowDays}
            disableRecalculate={anySuppressionJobRunning}
            onClickSuppressAll={handleSuppressAll}
            disableSuppressAll={
              suppressSubmitting || filteredOverlaps.length === 0
            }
          />
          <OverlapTable
            campaign={campaign}
            filteredOverlaps={filteredOverlaps}
            selectedOverlapIds={selectedOverlapIds}
            setSelectedOverlapIds={setSelectedOverlapIds}
            onSuppressSelected={handleSuppressSelected}
            suppressSubmitting={suppressSubmitting}
            anySuppressionJobRunning={anySuppressionJobRunning}
            onOpenFilterDialog={() => setFilterDialogOpen(true)}
            createdAtWindowChanged={createdAtWindowChanged}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </AccordionDetails>
        <OverlapFilterDialog
          open={filterDialogOpen}
          onSubmit={handleSuppressFiltered}
          onClose={() => setFilterDialogOpen(false)}
          selectedOverlapIds={selectedOverlapIds}
        />
      </StyledAccordion>
    </>
  );
}

function applyFilters({
  overlaps,
  search,
  sort,
  statusFilter,
}: {
  overlaps: CampaignForEditor["campaignOverlaps"];
  statusFilter: CampaignStatus[];
  search: string;
  sort: CampaignSortBy;
}): CampaignForEditor["campaignOverlaps"] {
  let filteredOverlaps = overlaps.filter(overlap => {
    if (overlap.appliedAt) {
      // already applied
      return false;
    }

    if (overlap.overlapCount - overlap.alreadySuppressedOverlapCount === 0) {
      // no new overlaps
      return false;
    }

    return statusFilter.includes(overlap.overlappingCampaign.status);
  });

  if (search) {
    const normalizedSearch = search.toLowerCase();
    filteredOverlaps = filteredOverlaps.filter(
      overlap =>
        overlap.overlappingCampaign.name
          .toLowerCase()
          .includes(normalizedSearch) ||
        (/^\d+$/.test(normalizedSearch.trim()) &&
          overlap.overlappingCampaign.shortId === Number(normalizedSearch))
    );
  }

  switch (sort) {
    case CampaignSortBy.CreatedAtAsc:
      return sortBy(
        filteredOverlaps,
        overlap => overlap.overlappingCampaign.createdAt
      );
    case CampaignSortBy.CreatedAtDesc:
      return sortBy(
        filteredOverlaps,
        overlap => overlap.overlappingCampaign.createdAt
      ).reverse();
    case CampaignSortBy.NameAsc:
      return sortBy(
        filteredOverlaps,
        overlap => overlap.overlappingCampaign.name
      );
    case CampaignSortBy.NameDesc:
      return sortBy(
        filteredOverlaps,
        overlap => overlap.overlappingCampaign.name
      ).reverse();
  }
}
